import { isEmpty } from 'lodash';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { search } from 'sr-types';
import type { Activity } from 'sr-types/lib/search/v1/graphql';
import { UserContext } from '../common/auth/UserContext';
import { FETCH_POLICY_CONFIG } from '../common/constants/constants';
import { ENTITY_CONSTANTS } from '../common/constants/entityConstants';
import { useConfigProductionScheduleRefetchNeeded } from '../common/utils/atoms/useConfigProductionScheduleRefetchNeeded';
import { failedToastMessage, successToastMessage } from '../common/utils/commonUtils';
import {
    activitySearchByPage,
    productionSearchByPage,
    resourceSearchByPage
} from '../common/utils/incrementalSearchPages';
import {
    client as productionClient,
    schedulerDetailsQuery as productionSchedulerDetailsQuery
} from '../production/helpers/production';
import { fetchProductionActivities } from './planningSchedulerApi';
import { fetchTimelineUpdates } from './fetchTimelineUpdates';
import {
    applyPlanningTimelineUpdates,
    dependencyIsForEvents,
    reconcilePlanningTimelineUpdates,
    replaceTimelineEvents,
    type ApplyTimelineEventChanges
} from './timelineUpdates';
import {
    buildPlanningStoreData,
    buildSyncMetaUpdates,
    countActivitiesByProduction,
    isProductionAvailableInCache,
    mergePlanningSchedulerStoreData
} from './planningSchedulerCache';
import type { PlanningSchedulerStoreData } from './planningSchedulerCache';
import { getCachedData, PRODUCTION_DEFAULT_FILTERS, UpdateIDB } from './scheduler';
import { makeProductionFilterPredicate } from './productionTimelineFilters';
import {
    BRYNTUM_EVENT_FIELDS,
    SCHEDULER_INDEX_DB_STATUS,
    SCHEDULER_INDEX_DB_TABLE,
    SCHEDULER_SEARCH_BATCH_SIZE
} from './schedulerConstants';
import {
    getCalendars,
    getHoursSince,
    getPlanningStoreDelta,
    matchesResourceFilterData,
    toBryntumCalendars,
    toBryntumDependenciesForActivity,
    toBryntumEventsAndAssignmentsForActivity,
    toLineItemsFromProductionActivity,
    toValidDate
} from './schedulerUtil';

type PlanningSchedulerDataContextType = {
    schedulerData: any;
    batchSchedulerStoreData: any;
    productionFilterFacets: search.SkFacetSet[];
    filterSchedulerData: any;
    isInitialized: boolean;
    dataLoaded: boolean;
    isEnabled: boolean;
    ensureInitialized: () => void;
    ensureProductionLoaded: (productionId: string) => Promise<boolean>;
    fetchUpdatedActivities: () => Promise<void>;
    applyTimelineEventChanges: ApplyTimelineEventChanges;
};

type PlanningSchedulerDataResult = {
    updates: PlanningSchedulerStoreData;
    results: Activity[];
    requestedData: PlanningSchedulerStoreData;
    requestedMutationRevisions: ReadonlyMap<string, number>;
};

export const PlanningSchedulerDataContext = createContext<PlanningSchedulerDataContextType>(undefined);

export const usePlanningSchedulerData = () => {
    const context = useContext(PlanningSchedulerDataContext);

    useEffect(() => {
        context?.ensureInitialized?.();
    }, [context?.ensureInitialized]);

    return context;
};

export const getLatestActivityData = (activeOrganizationAccount, lastUpdatedDate, signal?: AbortSignal) => {
    const activityUpdates = fetchTimelineUpdates(
        ENTITY_CONSTANTS.activity,
        activeOrganizationAccount,
        lastUpdatedDate,
        signal
    );
    return activityUpdates;
};

export const PlanningSchedulerDataProvider = ({ children }) => {
    const { activeOrganizationAccount, application, isAuthenticated } = useContext(UserContext);
    const [isSchedulerProductionRefetchNeeded] = useConfigProductionScheduleRefetchNeeded();
    const [schedulerData, setSchedulerData] = useState(undefined);
    const [activityData, setActivityData] = useState([]);
    const [batchActivityData, setbatchActivityData] = useState([]);
    const [batchSchedulerStoreData, setBatchSchedulerStoreData] = useState(undefined);
    const [productionData, setProductionData] = useState(undefined);
    const [batchProductionData, setBatchProductionData] = useState(undefined);
    const [productionFilterFacets, setProductionFilterFacets] = useState<search.SkFacetSet[]>([]);
    const [resourceData, setResourceData] = useState(undefined);
    const [batchResourceData, setBatchResourceData] = useState(undefined);
    const [calendarsData, setCalendarsData] = useState(undefined);
    const [dataLoaded, setDataLoaded] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    const schedulerLoadGenerationRef = useRef(0);
    const productionReadyPromisesRef = useRef<Map<string, Promise<boolean>>>(new Map());
    const calendarsDataRef = useRef<any[] | undefined>(undefined);
    const schedulerDataRef = useRef<PlanningSchedulerStoreData | undefined>(undefined);
    const eventMutationRevisionsRef = useRef(new Map<string, number>());
    const schedulerOwnerRef = useRef<string | undefined>(undefined);
    const refreshPromiseRef = useRef<Promise<void> | undefined>(undefined);
    const mountedRef = useRef(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isEnabled, setIsEnabled] = useState(false);

    useEffect(() => {
        calendarsDataRef.current = calendarsData;
    }, [calendarsData]);

    const resetStates = useCallback(() => {
        setSchedulerData(undefined);
        setActivityData([]);
        setbatchActivityData([]);
        setBatchSchedulerStoreData(undefined);
        setProductionData(undefined);
        setBatchProductionData(undefined);
        setProductionFilterFacets([]);
        setResourceData(undefined);
        setBatchResourceData(undefined);
        setCalendarsData(undefined);
        setDataLoaded(false);
        setIsInitialized(false);
        schedulerDataRef.current = undefined;
        eventMutationRevisionsRef.current = new Map();
    }, []);

    const ensureInitialized = useCallback(() => {
        setIsEnabled(true);
    }, []);

    const applyTimelineEventChanges: ApplyTimelineEventChanges = useCallback(
        async (updates, eventIds) => {
            if (!activeOrganizationAccount) return;

            if (mountedRef.current && schedulerOwnerRef.current === activeOrganizationAccount) {
                // Track absent-record deletions too, before a pending refresh can commit.
                for (const eventId of new Set(eventIds)) {
                    const revision = eventMutationRevisionsRef.current.get(eventId) ?? 0;
                    eventMutationRevisionsRef.current.set(eventId, revision + 1);
                }
                const nextStoreData = applyPlanningTimelineUpdates(schedulerDataRef.current, updates, eventIds);
                // Navigation and subsequent refreshes must see deletions before persistence completes.
                schedulerDataRef.current = nextStoreData;
                setSchedulerData(nextStoreData);
                // The live scheduler only needs this mutation; navigation reads the full schedulerData snapshot.
                const changedIds = new Set(eventIds);
                const dependencies =
                    updates.dependencies ??
                    (nextStoreData.dependencies || []).filter((dependency) =>
                        dependencyIsForEvents(dependency, changedIds)
                    );
                setBatchSchedulerStoreData({ ...updates, dependencies, replaceEventIds: eventIds });
            }
            // Persist to the callback's captured account even after navigation changes or unmounts its live scope.
            try {
                await UpdateIDB(
                    activeOrganizationAccount,
                    SCHEDULER_INDEX_DB_TABLE.schedulerProductionData,
                    updates,
                    eventIds
                );
            } catch (error) {
                // The server change has succeeded; a cache failure must not restore the deleted activity.
                console.error('Failed to persist planning timeline event changes to IndexedDB:', error);
            }
        },
        [activeOrganizationAccount]
    );

    const applyPlanningSnapshot = useCallback(
        (snapshot: PlanningSchedulerStoreData, overrides: Partial<PlanningSchedulerStoreData> = {}) => {
            const currentStoreData = schedulerDataRef.current;
            const incomingSnapshot = { ...snapshot, ...overrides };
            // Initial loading republishes accumulated pages. The shared ref owns any activities mutated since load start.
            const mutatedEventIds = [...eventMutationRevisionsRef.current.keys()];
            const safeSnapshot = mutatedEventIds.length
                ? replaceTimelineEvents(
                      incomingSnapshot,
                      { events: [], assignments: [], dependencies: [] },
                      mutatedEventIds
                  )
                : incomingSnapshot;
            const deltaStoreData = getPlanningStoreDelta(currentStoreData, safeSnapshot);
            const mergedStoreData = mergePlanningSchedulerStoreData(currentStoreData, safeSnapshot);

            const hasDelta =
                deltaStoreData.events.length > 0 ||
                deltaStoreData.resources.length > 0 ||
                deltaStoreData.assignments.length > 0 ||
                deltaStoreData.dependencies.length > 0 ||
                !isEmpty(deltaStoreData.sync_meta);

            const hasFetchTimestampOverride =
                overrides.lastFetchedAt !== undefined || overrides.eventsFetchedAt !== undefined;

            if (!hasDelta && !hasFetchTimestampOverride) {
                return;
            }

            schedulerDataRef.current = mergedStoreData;
            setSchedulerData(mergedStoreData);

            const shouldReplayMergedSnapshot =
                !(currentStoreData?.resources || []).length && (mergedStoreData.resources || []).length > 0;
            setBatchSchedulerStoreData(shouldReplayMergedSnapshot ? mergedStoreData : deltaStoreData);
            UpdateIDB(activeOrganizationAccount, SCHEDULER_INDEX_DB_TABLE.schedulerProductionData, mergedStoreData);
        },
        [activeOrganizationAccount]
    );

    const updateSchedulerStoreState = useCallback(
        (storeData: PlanningSchedulerStoreData, shouldUpdateBatchStore = false) => {
            if (!mountedRef.current) return;

            const mergedStoreData = mergePlanningSchedulerStoreData(schedulerDataRef.current, storeData);
            schedulerDataRef.current = mergedStoreData;
            setSchedulerData(mergedStoreData);

            if (shouldUpdateBatchStore) {
                setBatchSchedulerStoreData(storeData);
            }
        },
        []
    );

    const persistPlanningStoreData = useCallback(
        async (storeData: PlanningSchedulerStoreData, shouldUpdateBatchStore = false) => {
            updateSchedulerStoreState(storeData, shouldUpdateBatchStore);
            try {
                await UpdateIDB(activeOrganizationAccount, SCHEDULER_INDEX_DB_TABLE.schedulerProductionData, storeData);
            } catch (error) {
                console.error('Failed to persist planning scheduler data to IndexedDB:', error);
            }
        },
        [activeOrganizationAccount, updateSchedulerStoreState]
    );

    const ensureCalendarsLoaded = useCallback(
        async (cachedStoreData?: PlanningSchedulerStoreData) => {
            const currentCalendars = calendarsDataRef.current;
            if (Array.isArray(currentCalendars)) {
                return currentCalendars;
            }

            if (Array.isArray(cachedStoreData?.calendars)) {
                if (mountedRef.current && !Array.isArray(calendarsDataRef.current)) {
                    setCalendarsData(cachedStoreData.calendars);
                }
                return cachedStoreData.calendars;
            }

            const calendarResponse = await getCalendars(activeOrganizationAccount);
            const calendarItems = calendarResponse?.data?.results?.hits?.items || [];
            const nextCalendars = toBryntumCalendars(calendarItems);

            if (mountedRef.current && !Array.isArray(calendarsDataRef.current)) {
                setCalendarsData(nextCalendars);
            }

            await UpdateIDB(activeOrganizationAccount, SCHEDULER_INDEX_DB_TABLE.schedulerProductionData, {
                calendars: nextCalendars
            });

            return nextCalendars;
        },
        [activeOrganizationAccount]
    );

    const loadProductionIntoScheduler = useCallback(
        async (productionId: string, markReady: (value: boolean) => void) => {
            const loadGeneration = schedulerLoadGenerationRef.current;
            const signal = abortRef.current?.signal;
            const isLoadCancelled = () =>
                signal?.aborted || !mountedRef.current || loadGeneration !== schedulerLoadGenerationRef.current;

            try {
                const cachedStoreData = (await getCachedData(
                    activeOrganizationAccount,
                    SCHEDULER_INDEX_DB_TABLE.schedulerProductionData
                )) as PlanningSchedulerStoreData;

                if (isLoadCancelled()) {
                    markReady(false);
                    return;
                }

                if (isProductionAvailableInCache(cachedStoreData, productionId)) {
                    const isProductionInState = isProductionAvailableInCache(schedulerDataRef.current, productionId);

                    if (mountedRef.current && !isProductionInState && !isEmpty(cachedStoreData)) {
                        setCalendarsData(cachedStoreData?.calendars);
                        updateSchedulerStoreState(cachedStoreData);
                    }

                    markReady(isProductionInState || !isEmpty(cachedStoreData));
                    return;
                }

                const calendars = await ensureCalendarsLoaded(cachedStoreData);

                if (isLoadCancelled()) {
                    markReady(false);
                    return;
                }

                const [productionResponse, activitiesResponse] = await Promise.all([
                    productionClient.query({
                        query: productionSchedulerDetailsQuery,
                        variables: {
                            id: productionId
                        },
                        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
                        context: {
                            headers: {
                                ownerId: activeOrganizationAccount
                            },
                            fetchOptions: { signal }
                        }
                    }),
                    fetchProductionActivities(activeOrganizationAccount, productionId, signal)
                ]);

                if (isLoadCancelled()) {
                    markReady(false);
                    return;
                }

                const production = productionResponse?.data?.production;
                if (!production) {
                    markReady(false);
                    return;
                }

                const productionActivities = activitiesResponse;
                const activityCount = productionActivities.length;
                const syncCompletedAt = new Date();
                const completedSyncMeta = buildSyncMetaUpdates(
                    [production],
                    {
                        [productionId]: activityCount
                    },
                    true,
                    cachedStoreData?.sync_meta || {},
                    syncCompletedAt
                );

                const completedStoreData = buildPlanningStoreData({
                    productions: [production],
                    resources: [],
                    activities: productionActivities,
                    application,
                    calendars,
                    syncMeta: completedSyncMeta,
                    timestamp: syncCompletedAt
                });

                if (isLoadCancelled()) {
                    markReady(false);
                    return;
                }

                await persistPlanningStoreData(completedStoreData, true);
                markReady(true);
            } catch (error) {
                console.error(`Error fetching production timeline data for ${productionId}:`, error);
                markReady(false);
            } finally {
                productionReadyPromisesRef.current.delete(productionId);
            }
        },
        [
            activeOrganizationAccount,
            application,
            ensureCalendarsLoaded,
            persistPlanningStoreData,
            updateSchedulerStoreState
        ]
    );

    const ensureProductionLoaded = useCallback(
        async (productionId: string) => {
            if (!productionId || !activeOrganizationAccount || !application) {
                return false;
            }

            const existingPromise = productionReadyPromisesRef.current.get(productionId);
            if (existingPromise) {
                return existingPromise;
            }

            let resolveReady: (value: boolean) => void = () => {};
            let readyResolved = false;
            const markReady = (value: boolean) => {
                if (!readyResolved) {
                    readyResolved = true;
                    resolveReady(value);
                }
            };

            const readyPromise = new Promise<boolean>((resolve) => {
                resolveReady = resolve;
            });

            productionReadyPromisesRef.current.set(productionId, readyPromise);

            void loadProductionIntoScheduler(productionId, markReady);

            return readyPromise;
        },
        [activeOrganizationAccount, application, loadProductionIntoScheduler]
    );

    const getData = useCallback(
        async (
            schedulerStoreData: PlanningSchedulerStoreData,
            signal?: AbortSignal
        ): Promise<PlanningSchedulerDataResult> => {
            const requestedMutationRevisions = new Map(eventMutationRevisionsRef.current);
            const fetchedAt = new Date();
            const results = await getLatestActivityData(
                activeOrganizationAccount,
                schedulerStoreData.lastFetchedAt,
                signal
            );
            let updates: PlanningSchedulerStoreData = {};
            if (results.length) {
                successToastMessage('timeline.refetch.message', { count: results.length });
                const schedulerLineItems = toLineItemsFromProductionActivity(results, application.name) || [];
                const { events, assignments } = toBryntumEventsAndAssignmentsForActivity(
                    schedulerLineItems,
                    application
                );
                const { dependencies } = toBryntumDependenciesForActivity(schedulerLineItems);

                updates = { events, assignments, dependencies, lastFetchedAt: fetchedAt };
            }
            signal?.throwIfAborted();
            const latestSchedulerData = {
                updates,
                results,
                requestedData: schedulerStoreData,
                requestedMutationRevisions
            };
            return latestSchedulerData;
        },
        [activeOrganizationAccount, application]
    );

    const commitPlanningActivityUpdates = useCallback(
        async (
            { updates, results, requestedData, requestedMutationRevisions }: PlanningSchedulerDataResult,
            cachedStoreData?: PlanningSchedulerStoreData
        ) => {
            // A cached snapshot may predate mutations received during restoration. The shared ref owns those IDs.
            const safeCachedStoreData = cachedStoreData
                ? replaceTimelineEvents(cachedStoreData, { events: [], assignments: [], dependencies: [] }, [
                      ...eventMutationRevisionsRef.current.keys()
                  ])
                : undefined;
            const currentStoreData = cachedStoreData
                ? mergePlanningSchedulerStoreData(safeCachedStoreData, schedulerDataRef.current)
                : schedulerDataRef.current;
            const eventIds = results.map((item) => item.id);
            const reconciledData = reconcilePlanningTimelineUpdates(
                requestedData,
                currentStoreData,
                updates,
                eventIds,
                requestedMutationRevisions,
                eventMutationRevisionsRef.current
            );
            const nextStoreData = reconciledData.formattedData;
            schedulerDataRef.current = nextStoreData;
            setSchedulerData(nextStoreData);
            const batchSchedulerData = {
                ...(cachedStoreData ? nextStoreData : reconciledData.updates),
                replaceEventIds: reconciledData.replaceEventIds
            };
            setBatchSchedulerStoreData(batchSchedulerData);
            // Queue the cache write after reconciliation, in the same order as in-memory mutations.
            if (results.length) {
                try {
                    await UpdateIDB(
                        activeOrganizationAccount,
                        SCHEDULER_INDEX_DB_TABLE.schedulerProductionData,
                        reconciledData.updates,
                        reconciledData.replaceEventIds
                    );
                } catch (error) {
                    console.error('Failed to persist reconciled planning timeline updates to IndexedDB:', error);
                }
            }
            return nextStoreData;
        },
        [activeOrganizationAccount]
    );

    const fetchUpdatedActivities = useCallback((): Promise<void> => {
        if (!isInitialized || !schedulerDataRef.current || schedulerOwnerRef.current !== activeOrganizationAccount) {
            const completedRefresh = Promise.resolve();
            return completedRefresh;
        }
        if (refreshPromiseRef.current) return refreshPromiseRef.current;
        const signal = abortRef.current?.signal;
        const refreshTimelineUpdates = async () => {
            try {
                const latestData = await getData(schedulerDataRef.current, signal);
                if (!mountedRef.current || signal?.aborted || !latestData.results.length) return;
                await commitPlanningActivityUpdates(latestData);
            } catch (error) {
                if (!mountedRef.current || signal?.aborted) return;
                console.error('Error refreshing planning timeline:', error);
                failedToastMessage('form.load.failed.message');
            } finally {
                if (refreshPromiseRef.current === refresh) refreshPromiseRef.current = undefined;
            }
        };
        const refresh = refreshTimelineUpdates();
        refreshPromiseRef.current = refresh;
        return refresh;
    }, [activeOrganizationAccount, commitPlanningActivityUpdates, getData, isInitialized]);

    useEffect(() => {
        if (!isEnabled || !isAuthenticated || !activeOrganizationAccount || !application) {
            return;
        }

        resetStates();
        schedulerOwnerRef.current = activeOrganizationAccount;
        refreshPromiseRef.current = undefined;
        mountedRef.current = true;
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        const productionReadyPromises = productionReadyPromisesRef.current;
        const signal = abortRef.current.signal;
        getCachedData(activeOrganizationAccount, SCHEDULER_INDEX_DB_TABLE.schedulerProductionData).then((data) => {
            const isCacheEmpty = isEmpty(data);
            const lastFetchedAtDate = toValidDate(data?.lastFetchedAt);
            const hasLastFetchedAt = !!lastFetchedAtDate && !isNaN(lastFetchedAtDate.getTime());
            const hoursSinceLastFetch = !isCacheEmpty ? getHoursSince(lastFetchedAtDate) : undefined;
            const isCacheStale = !isCacheEmpty && hoursSinceLastFetch > 1;

            const hasNoEvents = !isCacheEmpty && isEmpty(data.events);
            const eventsNeverFetched = !isCacheEmpty && !data.eventsFetchedAt;

            const activitiesFetchState = data?.fetchMeta?.state;
            const isActivitiesFetchComplete = activitiesFetchState === SCHEDULER_INDEX_DB_STATUS.complete;

            const shouldRefetch =
                isCacheEmpty ||
                !hasLastFetchedAt ||
                isCacheStale ||
                (hasNoEvents && eventsNeverFetched) ||
                !isActivitiesFetchComplete;

            const productionFilters = [{ filters: PRODUCTION_DEFAULT_FILTERS }];
            if (shouldRefetch) {
                const calendarsPromise = getCalendars(activeOrganizationAccount);
                UpdateIDB(activeOrganizationAccount, SCHEDULER_INDEX_DB_TABLE.schedulerProductionData, {
                    fetchMeta: {
                        ...(data?.fetchMeta || {}),
                        state: SCHEDULER_INDEX_DB_STATUS.in_progress,
                        updatedAt: new Date()
                    }
                });

                Promise.all([calendarsPromise]).then((res) => {
                    if (!mountedRef.current || signal.aborted) return;
                    const calendarData = res[0].data?.results?.hits?.items || [];
                    setCalendarsData(toBryntumCalendars(calendarData));
                    const resourcePromise = Promise.all(
                        resourceSearchByPage(
                            activeOrganizationAccount,
                            SCHEDULER_SEARCH_BATCH_SIZE,
                            [],
                            setBatchResourceData,
                            setResourceData,
                            2,
                            false,
                            undefined,
                            signal
                        )
                    ).then((res) => {
                        if (!mountedRef.current || signal.aborted) return;
                        const resourceData = res.flat();
                        setResourceData(resourceData);
                    });

                    const productionPromise = Promise.all(
                        productionSearchByPage(
                            activeOrganizationAccount,
                            SCHEDULER_SEARCH_BATCH_SIZE,
                            productionFilters,
                            setBatchProductionData,
                            setProductionData,
                            2,
                            false,
                            undefined,
                            signal,
                            (response) => {
                                if (!mountedRef.current || signal.aborted) return;
                                setProductionFilterFacets(response?.data?.results?.facets ?? []);
                            }
                        )
                    ).then((res) => {
                        if (!mountedRef.current || signal.aborted) return;
                        const productions = res.flat();
                        setProductionData(productions);
                    });

                    const activityPromises = Promise.all(
                        activitySearchByPage(
                            activeOrganizationAccount,
                            SCHEDULER_SEARCH_BATCH_SIZE,
                            [],
                            setbatchActivityData,
                            setActivityData,
                            2,
                            true,
                            undefined,
                            signal
                        )
                    ).then((res) => {
                        if (!mountedRef.current || signal.aborted) return;
                        const activityData = res.flatMap((activity) => activity);
                        setActivityData(activityData);
                    });

                    Promise.all([resourcePromise, productionPromise, activityPromises]).finally(() => {
                        if (!mountedRef.current || signal.aborted) return;
                        setDataLoaded(true);
                    });
                });
            } else {
                const loadCachedSchedulerData = async () => {
                    let nextData: PlanningSchedulerDataResult = {
                        updates: {},
                        results: [],
                        requestedData: data,
                        requestedMutationRevisions: new Map(eventMutationRevisionsRef.current)
                    };
                    try {
                        nextData = await getData(data, signal);
                    } catch (error) {
                        if (!mountedRef.current || signal.aborted) return;
                        console.error('Error refreshing cached planning timeline:', error);
                        failedToastMessage('form.load.failed.message');
                    }
                    if (!mountedRef.current || signal.aborted) return;
                    const nextStoreData = await commitPlanningActivityUpdates(nextData, data);
                    if (!mountedRef.current || signal.aborted) return;
                    setCalendarsData(nextStoreData.calendars);
                    setIsInitialized(true);
                    setDataLoaded(true);
                };
                loadCachedSchedulerData();
            }
        });
        return () => {
            schedulerLoadGenerationRef.current += 1;
            mountedRef.current = false;
            abortRef.current?.abort();
            abortRef.current = null;
            productionReadyPromises.clear();
        };
    }, [
        isEnabled,
        isAuthenticated,
        activeOrganizationAccount,
        application,
        commitPlanningActivityUpdates,
        getData,
        resetStates
    ]);

    useEffect(() => {
        if (
            Array.isArray(batchProductionData) &&
            Array.isArray(batchResourceData) &&
            Array.isArray(batchActivityData) &&
            calendarsData
        ) {
            const activityCountsByProduction = countActivitiesByProduction(batchActivityData);
            const syncMeta = buildSyncMetaUpdates(
                batchProductionData,
                activityCountsByProduction,
                false,
                schedulerDataRef.current?.sync_meta || {}
            );
            const storeData = buildPlanningStoreData({
                productions: batchProductionData,
                resources: batchResourceData,
                activities: batchActivityData,
                application,
                calendars: calendarsData,
                syncMeta,
                timestamp: new Date()
            });
            applyPlanningSnapshot(storeData);
        }
    }, [applyPlanningSnapshot, batchProductionData, calendarsData, batchActivityData, batchResourceData, application]);

    useEffect(() => {
        if (productionData && resourceData && activityData && calendarsData) {
            const activityCountsByProduction = countActivitiesByProduction(activityData);
            const syncCompletedAt = new Date();
            const syncMeta = buildSyncMetaUpdates(
                productionData,
                activityCountsByProduction,
                true,
                schedulerDataRef.current?.sync_meta || {},
                syncCompletedAt
            );
            const nextStoreData = mergePlanningSchedulerStoreData(schedulerDataRef.current, {
                sync_meta: syncMeta,
                lastFetchedAt: syncCompletedAt,
                eventsFetchedAt: syncCompletedAt
            });
            schedulerDataRef.current = nextStoreData;
            setSchedulerData(nextStoreData);
            UpdateIDB(activeOrganizationAccount, SCHEDULER_INDEX_DB_TABLE.schedulerProductionData, nextStoreData);
        }
    }, [productionData, calendarsData, activityData, resourceData, activeOrganizationAccount]);

    useEffect(() => {
        let cancelled = false;

        if (isSchedulerProductionRefetchNeeded) {
            const refreshSchedulerData = async () => {
                try {
                    const data = await getCachedData(
                        activeOrganizationAccount,
                        SCHEDULER_INDEX_DB_TABLE.schedulerProductionData
                    );
                    if (cancelled || isEmpty(data) || !schedulerDataRef.current) return;

                    const nextStoreData = {
                        ...schedulerDataRef.current,
                        assignments: [...(data.assignments || [])],
                        events: [...(data.events || [])],
                        dependencies: [...(data.dependencies || [])]
                    };
                    schedulerDataRef.current = nextStoreData;
                    setSchedulerData(nextStoreData);
                    // Remounted timelines replay this batch after loading schedulerData.
                    setBatchSchedulerStoreData(nextStoreData);
                } catch (error) {
                    if (cancelled) return;
                    console.error('Failed to refresh planning scheduler data from IndexedDB:', error);
                }
            };

            refreshSchedulerData();
        }

        const cancelRefresh = () => {
            cancelled = true;
        };
        return cancelRefresh;
    }, [isSchedulerProductionRefetchNeeded, activeOrganizationAccount]);

    useEffect(() => {
        if (dataLoaded) {
            const completedAt = new Date();
            const timeout = setTimeout(() => {
                const currentStoreData = schedulerDataRef.current;
                if (!currentStoreData) return;
                UpdateIDB(activeOrganizationAccount, SCHEDULER_INDEX_DB_TABLE.schedulerProductionData, {
                    fetchMeta: {
                        ...(currentStoreData.fetchMeta || {}),
                        state: SCHEDULER_INDEX_DB_STATUS.complete,
                        completedAt: completedAt,
                        updatedAt: completedAt
                    }
                });
                setIsInitialized(true);
            }, 1000);

            return () => clearTimeout(timeout);
        }
    }, [dataLoaded, activeOrganizationAccount]);

    const calculateProductionCount = (schedulerInstance) => {
        const store = schedulerInstance?.resourceStore;
        if (!store) return 0;
        return store.count - (store.groupRecords?.length || 0);
    };

    const updateTotalCount = (filterType: string, ref, setResourceFilterData) => {
        if (filterType !== ENTITY_CONSTANTS.production.toLowerCase() || !ref?.current?.instance) return;
        const schedulerInstance = ref.current.instance;
        setResourceFilterData((prev) => {
            if (!isEmpty(prev)) prev.results.hits.page.total = calculateProductionCount(schedulerInstance);
            return { ...prev };
        });
    };

    const filterSchedulerData = async (
        filterType,
        variables,
        ref,
        shouldClearFilter,
        setShouldClearFilter,
        setResourceFilterData,
        baseResourceFilterData = {}
    ) => {
        if (!variables?.filters || !ref?.current || filterType !== ENTITY_CONSTANTS.production.toLowerCase()) return;

        const scheduler = ref.current.instance;
        const store = scheduler.resourceStore;

        const matchesProductionFilters = makeProductionFilterPredicate(variables.filters);

        if (shouldClearFilter) {
            await store.clearFilters();
        } else {
            setShouldClearFilter(true);
        }
        scheduler.suspendRefresh();
        await store.filter((record) => {
            const data = record.data;
            if (!matchesResourceFilterData(data, baseResourceFilterData)) {
                return false;
            }
            const matchesSelectedFilters = matchesProductionFilters(data);
            return matchesSelectedFilters;
        });

        updateTotalCount(filterType, ref, setResourceFilterData);
        scheduler.resumeRefresh(true);
    };

    return (
        <PlanningSchedulerDataContext.Provider
            value={{
                schedulerData,
                batchSchedulerStoreData,
                productionFilterFacets,
                filterSchedulerData,
                isInitialized,
                dataLoaded,
                isEnabled,
                ensureInitialized,
                ensureProductionLoaded,
                fetchUpdatedActivities,
                applyTimelineEventChanges
            }}
        >
            {children}
        </PlanningSchedulerDataContext.Provider>
    );
};
